import { useCallback, useEffect, useRef, useState } from "react";

// Internal Imports
import { useForm } from "../../contexts/form";
import IColumn from "../../interfaces/IColumn";
import { IMenu } from "../../interfaces/IMenu";
import { Mode } from "../../common/types/AllTypes";
import { IForm } from "../../interfaces/IForm";
import { getError, openNotification, tryCatch } from "../../common/constants/Utilities";
import { DeactiveFormByID, GetFormByID, GetForms} from "../../services/form";
import MenuLayout from "../../common/components/MenuLayout/MenuLayout";
import ViewContainer from "../../common/components/ViewContainer/ViewContainer";
import DataGrid from "../../common/components/DataGrid/DataGrid";
import ConfirmationModal from "../../common/components/Modal/CorfimationModal";
import Modal from "../../common/components/Modal/Modal";
import Paint from "../../common/components/Paint/Paint";
import { COLORS } from "../../common/constants/colors";
import AddUpdateFormulario from "./AddUpdateFormulario";
import { IResponse } from "../../interfaces/IResponse";
import RelacionarListadoUsuario from "./RelacionarListadoUsuario";
import RelacionarPreguntas from "./RelacionarPreguntas";
import { LoadingIcon } from "../../common/constants/cssElements";

import { useListDepartmentByUer } from '../../hooks/useListDepartment';

interface Props {
    menu: IMenu;
}

const Formularios: React.FC<Props> = ({ menu }) => {

    // States
    const { listDepartment, loadingDeparment } = useListDepartmentByUer();
    const [mode, setMode] = useState<Mode>('Add');
    const [searchV, setSearchV] = useState<string>('');
    const { formQuestion, setFormQuestion } = useForm();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [list_QuestionForm, setList_QuestionForm] = useState<IForm[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);

    // Modal
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [relateUserModal, setRelateUserModal] = useState<boolean>(false);
    const [relateQuestionModal, setRelateQuestionModal] = useState<boolean>(false);

    const groupDep = useRef<IForm>();

    // Consts
    const columns: IColumn[] = [
        {
            title: 'Formulario',
            dataIndex: 'nombre',
        },
        {
            title: 'Fecha de Inicio',
            dataIndex: '',
            render: (form: IForm) => new Date(form.fechaDesde!).toLocaleString()
        },
        {
            title: 'Fecha de Fin',
            dataIndex: '',
            render: (form: IForm) => new Date(form.fechaHasta!).toLocaleString()
        },
        {
            title: 'Vizualización',
            dataIndex: 'visualizacionTipoNombre',
        },
        {
            title: 'Secuencial',
            dataIndex: '',
            render: (form: IForm) => form.esFormularioSecuencial ? 'SI' : 'NO'
        },
        {
            title: 'Estado',
            dataIndex: 'formularioEstadoTipoNombre',
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoNombre',
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetForms(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (form: IForm) => loadingDetail && idDetail === form.formularioID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];

    // handles
    const handleGetForms = () => {
        tryCatch(() => {
            setLoading(true);
            GetForms()
                .then((response: IResponse) => {

                    const listListBase: IForm[] = response.data;
                    listListBase.forEach(qtGroup => {
                        qtGroup.departamentoNombre = listDepartment.find(dep => dep.departamentoGUID === qtGroup.departamentoGUID)?.nombre;
                    })

                    setList_QuestionForm(response.data);
                })
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => setLoading(false));
        })
    }


    const handleGetFormById = (form: IForm) => {
        setFormQuestion(undefined);
        groupDep.current = undefined;

        if (!!form) {
            setIdDetail(form.formularioID);
            setLoadingDetail(true);

            tryCatch(() => {
                GetFormByID(form.formularioID)
                    .then(({ data }: IResponse) => {
                        //Ordena las preguntas al hacer click en RadioButton
                        data.pregunta.sort((a: { orden: number; }, b: { orden: number; }) => a.orden - b.orden);
                       
                        setFormQuestion(data);
                        groupDep.current = form;
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
    }

    const handleDeactiveFormByID = () => {
        tryCatch(() => {
            setLoadingAction(true);
            DeactiveFormByID(formQuestion?.formularioID!)
                .then((response: IResponse) => {
                    openNotification('success', `Formulario (${formQuestion!.nombre}) eliminado`, response.msg!);
                    setList_QuestionForm(list_QuestionForm.filter(x => x.formularioID !== formQuestion?.formularioID));
                })
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => {
                    setLoadingAction(false);
                    setDeleteModal(false);
                    setFormQuestion(undefined);
                })
        })
    }

    // Methods
    const getFormsFiltered = useCallback((): IForm[] => {
        let formFiltered: IForm[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            formFiltered = list_QuestionForm.filter((form) => form.nombre.toLowerCase().includes(valueLowerCase) || form.fechaDesde?.toLowerCase().includes(valueLowerCase) || form.fechaHasta?.toLowerCase().includes(valueLowerCase) || form.visualizacionTipoNombre?.toLowerCase().includes(valueLowerCase) || form.formularioEstadoTipoNombre?.toLowerCase().includes(valueLowerCase) || form.departamentoNombre?.toLowerCase().includes(valueLowerCase));
        } else {
            formFiltered = list_QuestionForm;
        }

        return formFiltered;
    }, [searchV, list_QuestionForm]);

    const openModalUpdate = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Formulario tiene el Departamento Deshabilitado`, 'Contáctese con el equipo Bis para más información.');
        } else {
            setMode('Update');
            setAddUpdateModal(true);
        }
    }

    const openModalCopy = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Formulario tiene el Departamento Deshabilitado`, 'Contáctese cone el equipo Bis para más información')
        } else {
            setMode('Copy');
            setAddUpdateModal(true);
        }
    }

    const openModalCreate = () => {
        setMode('Add');
        setFormQuestion(undefined);
        setAddUpdateModal(true);
    }

    const openModalRealtedQuestion = () => {
        if (!formQuestion?.esFormularioSecuencial) {
            openNotification('error', 'Error', 'Este formulario NO ES SECUENCIAL, las (preguntas) no pueden ser relacionadas.');
        } else if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Formulario tiene el Departamento Deshabilitado`, 'Contáctese cone el equipo Bis para más información')
        }
        else {
            setRelateQuestionModal(true);
        }
    }

    const openModalUserAction = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Formulario tiene el Departamento Deshabilitado`, 'Contáctese cone el equipo Bis para más información')
        } else {
            setRelateUserModal(true);
        }
    }

    const resetTable = () => {
        setSelectedRowKeys([]);
        setFormQuestion(undefined);
    }
    //Effects
    useEffect(() => {
        handleGetForms();
    }, [, listDepartment]);

    useEffect(() => {
        if (!addUpdateModal) {
            resetTable();
        }
    }, [addUpdateModal]);

    useEffect(() => {
        if (!relateUserModal) {
            resetTable();
        }
    }, [relateUserModal]);

    useEffect(() => {
        if (!relateQuestionModal) {
            resetTable();
        }
    }, [relateQuestionModal]);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.listadoID + record.nombre }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getFormsFiltered()}
                    setItemSelected={(value: any) => handleGetFormById(value)}
                    addAction={{ active: true, method: () => openModalCreate() }}
                    deleteAction={{ active: !!formQuestion, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!formQuestion, method: () => openModalUpdate() }}
                    copyAction={{active: !!formQuestion, method: () => openModalCopy()}}
                    linkQuestionAction={{ active: !!formQuestion, method: openModalRealtedQuestion }}
                    linkUserAction={{ active: !!formQuestion, method: () => openModalUserAction() }}
                />
            </ViewContainer>


            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question="¿Seguro que deseas eliminar este Formulario?"
                method={handleDeactiveFormByID}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : (mode === 'Copy' ? 'Copiar' : 'Modificar')}  <Paint color={COLORS.blue_main(1)}>Formulario</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateFormulario dataList={list_QuestionForm} setDataList={setList_QuestionForm} mode={mode} setMode={setMode} setAddUpdateModal={setAddUpdateModal} listDepartment={listDepartment} loadingDeparment={loadingDeparment} />
            </Modal>

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {'Relacionar'}  <Paint color={COLORS.blue_main(1)}>Listado y Usuario</Paint></nav>} visible={relateUserModal} setVisible={setRelateUserModal} askBeforeClose={true}>
                <RelacionarListadoUsuario setRelateUserModal={setRelateUserModal} />
            </Modal>

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {'Relacionar'}  <Paint color={COLORS.blue_main(1)}>Preguntas</Paint></nav>} visible={relateQuestionModal} setVisible={setRelateQuestionModal} askBeforeClose={true}>
                <RelacionarPreguntas setRelateQuestionModal={setRelateQuestionModal} />
            </Modal>

        </MenuLayout>
    );
}


export default Formularios;